import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock, Users, TrendingUp } from "lucide-react";
import AppCard from "@/components/AppCard";
import { apps } from "@/data/apps";

const rotatingAppNames = apps.map((app) => app.name);

const HomePage = () => {
  const [activeAppIndex, setActiveAppIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveAppIndex((currentIndex) => (currentIndex + 1) % rotatingAppNames.length);
    }, 2400);

    return () => window.clearInterval(timer);
  }, []);

  const activeAppName = rotatingAppNames[activeAppIndex] || "Joshy";

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: { staggerChildren: 0.1 }
    }
  };

  const values = [
    { icon: <Clock className="w-6 h-6" />, title: "Move faster", description: "AI products that remove the busywork around learning, planning, and reflection." },
    { icon: <Users className="w-6 h-6" />, title: "Serve real people", description: "Joshy, ScribeMind, and Selah Daily are built around everyday use cases." },
    { icon: <TrendingUp className="w-6 h-6" />, title: "Create leverage", description: "Turn conversations, sermons, lectures, and local plans into action." },
    { icon: <Sparkles className="w-6 h-6" />, title: "Build with purpose", description: "A focused app portfolio from Aaron DPenha and The App Fam." }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center pt-20" data-testid="hero-section">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-4xl mx-auto"
          >
            <h1 className="heading-hero mb-6" data-testid="hero-title">
              <span className="block">The home of</span>
              <span className="relative block min-h-[1.15em] overflow-hidden bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeAppName}
                    className="block"
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -22 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {activeAppName}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl leading-relaxed text-zinc-500 font-normal w-full max-w-[18rem] sm:max-w-2xl mx-auto mb-10">
              The App Fam showcases practical AI products for real life: Joshy
              for Vancouver reservations and local discovery, ScribeMind for
              smarter studying, and Selah Daily for sermon-based devotionals by
              Aaron DPenha.
            </p>
            <div className="w-full max-w-[18rem] sm:max-w-none mx-auto flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/joshy" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-explore-btn">
                Explore Joshy
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/apps" className="btn-secondary inline-flex items-center justify-center" data-testid="hero-about-btn">
                View All Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apps Grid Section */}
      <section className="section-spacing bg-white" data-testid="apps-grid-section">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="caption mb-4">Our Apps</p>
            <h2 className="heading-2 mb-4">Built with purpose by Aaron DPenha.</h2>
            <p className="body-text max-w-xl mx-auto">
              Three focused solutions from The App Fam, each with a clear job to do.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="bento-grid"
          >
            {apps.map((app, index) => (
              <Link to={app.landingPath || "/apps"} key={app.id}>
                <AppCard app={app} index={index} />
              </Link>
            ))}
          </motion.div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link to="/apps" className="btn-link inline-flex items-center gap-2" data-testid="view-all-apps-link">
              View all apps
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing bg-[#F5F5F7]" data-testid="philosophy-section">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="caption mb-4">Our Philosophy</p>
            <h2 className="heading-2 mb-4">Technology with purpose.</h2>
            <p className="body-text max-w-2xl mx-auto">
              The App Fam exists to turn useful AI into products people can
              actually use, from booking a table in Vancouver to remembering a
              lecture or carrying a sermon through the week.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                variants={fadeInUp}
                className="feature-card text-center"
              >
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4 text-zinc-600">
                  {value.icon}
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">{value.title}</h3>
                <p className="text-sm text-zinc-500">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-zinc-900" data-testid="cta-section">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <h2 className="heading-2 text-white mb-4">Ready to get started?</h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto">
              Start with Joshy, or explore every Aaron DPenha product under The App Fam.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/joshy"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="cta-explore-btn"
              >
                Explore Joshy
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="h-12 px-8 rounded-full border border-zinc-700 text-white font-medium text-sm hover:bg-zinc-800 transition-all inline-flex items-center justify-center"
                data-testid="cta-contact-btn"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
